import { rem } from 'polished'
import type * as React from 'react'
import { FormattedMessage } from 'react-intl'
import styled, { css } from 'styled-components'

import type { CalendarSpanSegment } from './Calendar.types'
import CalendarSpanBar from './CalendarSpanBar'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LinePlaneIcon from 'LuxKit/Icons/line/LinePlaneIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'

import type { StopCalendarChip, StopCalendarSpanData } from './stopCalendarOverlays'

export const STOP_BAR_LABEL_COLOUR = 'var(--palette-neutral-surface-dark-contrast)'

const MAX_VISIBLE_CHIPS = 2

/** Below this a cell is roughly 60px wide, where a chip reads as noise. */
const CHIPS_MIN_CALENDAR_WIDTH = 460

/**
 * How loudly a bar should speak.
 *
 * `muted` is for the bars that are only there as context — the rest of the
 * trip shown behind a date choice being made about one stop. They keep their
 * colour so the itinerary is still legible at a glance, but step back far
 * enough that the stop actually being edited reads as the subject.
 */
export type StopCalendarSpanEmphasis = 'normal' | 'muted'

/**
 * How much room to leave where two stops change over on the same day.
 *
 * `handle` keeps the two drag handles' circular outlines clear of each other,
 * which costs 10px from each bar. Without handles to separate that clearance
 * is 20px of dead space in the middle of a continuous trip, and reads as the
 * itinerary being broken into disconnected pieces — `tight` leaves just
 * enough of a seam to tell the two stops apart.
 */
export type StopCalendarBoundaryGap = 'handle' | 'tight'

/** Per-side inset at a shared changeover day when no handles need clearing. */
const TIGHT_BOUNDARY_GAP_PX = 2

/** Keeps two stops apart when one starts the day after the other ends. */
const ADJACENT_BOUNDARY_GAP_PX = 8

/**
 * The stop's name, ready for the bar to drop straight in.
 *
 * Wrapped here rather than passed as bare text so it can ellipsise: a bar is
 * only as wide as the days it covers, and "Melbou" reads as a rendering fault
 * where "Melbo…" reads as a name that did not fit. The full name stays on the
 * element for hover.
 */
const BarLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const emphasised = css<{ $emphasis: StopCalendarSpanEmphasis }>`
  ${props => props.$emphasis === 'muted' && css`
    opacity: 0.55;
  `}
`

/**
 * A stop reads as the block of tiles it occupies, so its bar is square and
 * runs to the edges of its first and last day rather than tapering into them.
 */
/**
 * The bar dims and lightens a label that is not on the span's first week, to
 * mark it as continued. A name deliberately placed on a later week is the
 * span's only name, so that treatment is undone — and pushed clear of the
 * mask fading that edge, which would otherwise wash out its first letter.
 */
const labelledContinuation = css`
  &.has-stop-label > div > span {
    opacity: 1;
    font-weight: 600;
  }

  &.has-stop-label.fade-left,
  &.has-stop-label.fade-both {
    padding-inline-start: ${rem(15)};
  }
`

const StopBar = styled(CalendarSpanBar)<{ $emphasis: StopCalendarSpanEmphasis }>`
  ${emphasised}
  ${labelledContinuation}

  &.starts,
  &.ends {
    border-radius: 0;
  }
`

/** A booked hotel keeps its existing calendar-pill treatment while spanning its stay. */
const BookedAccommodationBar = styled(CalendarSpanBar)<{ $emphasis: StopCalendarSpanEmphasis }>`
  ${emphasised}
  ${labelledContinuation}
  border: 1px solid currentColor;
  font-size: ${rem(10.5)};
  font-weight: 500;

  &.starts {
    border-start-start-radius: ${rem(999)};
    border-end-start-radius: ${rem(999)};
  }

  &.ends {
    border-start-end-radius: ${rem(999)};
    border-end-end-radius: ${rem(999)};
  }
`

const Chips = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  min-width: 0;
  margin-top: ${rem(2)};

  @container lux-calendar (max-width: ${CHIPS_MIN_CALENDAR_WIDTH}px) {
    display: none;
  }
`

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(3)};
  font-size: ${rem(10.5)};
  line-height: ${rem(14)};
  padding: ${rem(1)} ${rem(5)};
  border-radius: ${rem(999)};
  border: 1px solid currentColor;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const MoreChips = styled.div`
  font-size: ${rem(10)};
  padding-inline-start: ${rem(5)};
  color: var(--lux-cal-muted);
`

function getChipIcon(itemType: StopCalendarChip['itemType']) {
  switch (itemType) {
    case 'ACCOMMODATION': return <LineBedIcon size="XS" />
    case 'FLIGHT': return <LinePlaneIcon size="XS" />
    default: return <LineTicketIcon size="XS" />
  }
}

interface SpanBarProps {
  segment: CalendarSpanSegment<StopCalendarSpanData>
  emphasis?: StopCalendarSpanEmphasis
  /** @default 'handle' */
  boundaryGap?: StopCalendarBoundaryGap
  /** Optional drag affordances at the span's real first and last day. */
  startHandle?: React.ReactNode
  endHandle?: React.ReactNode
}

/**
 * One week's slice of a stop, or of a hotel booked inside one.
 *
 * Shared by every calendar that draws the itinerary — the stop date editor
 * and the read-only trip context on a search calendar — so a stop looks the
 * same wherever someone meets it.
 */
export function StopCalendarSpanBar(props: SpanBarProps) {
  const { segment, emphasis = 'normal', boundaryGap = 'handle', startHandle, endHandle } = props
  const spanData = segment.span.data
  const bookedAccommodationSpan = spanData && 'kind' in spanData ? spanData : undefined
  const stopSpan = spanData && !('kind' in spanData) ? spanData : undefined
  /*
   * A tight boundary is drawn as a plain inset rather than by asking the bar
   * for its shared-boundary treatment, because that treatment's gap is sized
   * for the handles it exists to separate.
   */
  const isTightBoundary = boundaryGap === 'tight'
  const hasSharedStartBoundary = !isTightBoundary && !!stopSpan?.hasSharedStartBoundary
  const hasSharedEndBoundary = !isTightBoundary && !!stopSpan?.hasSharedEndBoundary
  const SpanBar = bookedAccommodationSpan ? BookedAccommodationBar : StopBar

  /*
   * A stop with no nights of its own, sitting between two stops that touch
   * it, would be asked for the half of a single day on each side — which is
   * nothing at all, and less than nothing once the boundary insets come off.
   * It takes the whole day instead, so a nightless stop is still on the
   * calendar rather than silently missing from the trip.
   */
  const isNightlessSharedStop = segment.isStart && segment.isEnd &&
    segment.columnSpan === 1 &&
    !!stopSpan?.hasSharedStartBoundary &&
    !!stopSpan?.hasSharedEndBoundary

  const startInsetPx = stopSpan?.hasSharedStartBoundary
    ? (isTightBoundary ? TIGHT_BOUNDARY_GAP_PX : 0)
    : (stopSpan?.hasAdjacentStartBoundary ? ADJACENT_BOUNDARY_GAP_PX : 0)
  const endInsetPx = stopSpan?.hasSharedEndBoundary
    ? (isTightBoundary ? TIGHT_BOUNDARY_GAP_PX : 0)
    : (stopSpan?.hasAdjacentEndBoundary ? ADJACENT_BOUNDARY_GAP_PX : 0)

  // The name goes on the widest slice, which is not always the one the stop
  // begins in. Decided in buildSpanLayout, where the siblings are visible.
  const carriesLabel = segment.isWidest
  const name = bookedAccommodationSpan?.label ?? stopSpan?.placeName
  const background = bookedAccommodationSpan
    ? bookedAccommodationSpan.colours.background
    : spanData?.colours.foreground ?? '#0F4C4C'
  const foreground = bookedAccommodationSpan
    ? bookedAccommodationSpan.colours.foreground
    : STOP_BAR_LABEL_COLOUR

  return <SpanBar
    $emphasis={emphasis}
    className={carriesLabel ? 'has-stop-label' : undefined}
    columnStart={segment.columnStart}
    columnSpan={segment.columnSpan}
    isStart={segment.isStart}
    isEnd={segment.isEnd}
    lane={segment.lane}
    /*
     * A stop fills the tiles it owns, edge to edge. Two exceptions: a
     * changeover day it shares with the neighbour is split down the middle,
     * because both have a genuine claim on it; and a stop whose neighbour
     * starts the very next day keeps a gap, or the two run together and read
     * as one stop.
     */
    startAtCellEdge={Boolean(bookedAccommodationSpan) || isNightlessSharedStop || !stopSpan?.hasSharedStartBoundary}
    startInsetPx={startInsetPx}
    endAtCellEdge={Boolean(bookedAccommodationSpan) || isNightlessSharedStop || !stopSpan?.hasSharedEndBoundary}
    endInsetPx={endInsetPx}
    hasSharedStartBoundary={hasSharedStartBoundary}
    hasSharedEndBoundary={hasSharedEndBoundary}
    background={background}
    foreground={foreground}
    // Name and icon travel together to whichever week has room for them.
    icon={bookedAccommodationSpan && carriesLabel ? <LineBedIcon size="XS" /> : undefined}
    label={carriesLabel && name
      ? <BarLabel title={name} data-testid={`calendar-span-${segment.span.id}`}>{name}</BarLabel>
      : undefined}
    startHandle={startHandle}
    endHandle={endHandle}
  />
}

interface DayChipsProps {
  chips: ReadonlyArray<StopCalendarChip>
}

/** What is already booked on a day, stacked under its number. */
export function StopCalendarDayChips({ chips }: DayChipsProps) {
  if (!chips.length) return null

  const visible = chips.slice(0, MAX_VISIBLE_CHIPS)
  const overflow = chips.length - visible.length

  return <Chips data-stop-day-chips>
    {visible.map(chip => (
      <Chip
        key={chip.id}
        style={{
          backgroundColor: chip.colours.background,
          color: chip.colours.foreground,
        }}
        title={chip.label}
      >
        {getChipIcon(chip.itemType)}
        {chip.label}
      </Chip>
    ))}
    {overflow > 0 && <MoreChips>
      <FormattedMessage
        id="gfehstO0zRMjf-ENFzOA"
        defaultMessage="+{count}"
        description="Count of further planned items on a trip planner calendar day that did not fit in the tile"
        values={{ count: overflow }}
      />
    </MoreChips>}
  </Chips>
}
